import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiRefreshCw, FiTool, FiShield, FiUsers, FiBookOpen } = FiIcons;

const themes = [
  {
    icon: FiRefreshCw,
    title: "Consistency Over Spectacle",
    description: "Reliability is one of the most romantic qualities a partner can have. Showing up, day after day, is its own kind of devotion.",
    color: "bg-amber-50 text-amber-700",
    border: "border-amber-100"
  },
  {
    icon: FiTool,
    title: "Service as Love",
    description: "Everyday help — fixing, handling chores, anticipating needs — is not just practicality. It is emotional investment made visible.",
    color: "bg-rose-50 text-rose-700",
    border: "border-rose-100"
  },
  {
    icon: FiShield,
    title: "Protection & Safety",
    description: "Guarding your peace, your time, and your future is a form of devotion that runs deeper than any romantic gesture.",
    color: "bg-sky-50 text-sky-700",
    border: "border-sky-100"
  },
  {
    icon: FiUsers,
    title: "Presence Over Performance",
    description: "Choosing to sit in silence with you, to simply be near, is one of the most powerful romantic acts a person can offer.",
    color: "bg-emerald-50 text-emerald-700",
    border: "border-emerald-100"
  },
  {
    icon: FiBookOpen,
    title: "Being Truly Seen",
    description: "Remembering your preferences, your fears, your small joys — this is evidence of deep, attentive love that never stops paying attention.",
    color: "bg-violet-50 text-violet-700",
    border: "border-violet-100"
  }
];

const CoreThemes = () => {
  return (
    <section className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center max-w-2xl mx-auto mb-16"
        >
          <span className="text-brand-600 font-semibold tracking-widest uppercase text-sm mb-4 block">
            The 5 Core Themes
          </span>
          <h2 className="text-3xl md:text-5xl font-serif font-bold text-stone-900 mb-6">
            A New Map for Understanding Love
          </h2>
          <p className="text-lg text-stone-600">
            Each chapter of this book explores one dimension of quiet devotion — the kind that builds a lasting, unbreakable bond.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-6">
          {themes.slice(0, 3).map((theme, i) => (
            <ThemeCard key={i} theme={theme} index={i} />
          ))}
        </div>
        <div className="grid md:grid-cols-2 gap-6 mt-6 md:max-w-[calc(66.66%+12px)] mx-auto">
          {themes.slice(3).map((theme, i) => (
            <ThemeCard key={i + 3} theme={theme} index={i + 3} />
          ))}
        </div>
      </div>
    </section>
  );
};

const ThemeCard = ({ theme, index }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ delay: index * 0.08 }}
    className={`p-8 rounded-2xl border ${theme.border} bg-white hover:shadow-lg transition-shadow duration-300 group`}
  >
    <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-5 ${theme.color} bg-opacity-20`}>
      <SafeIcon icon={theme.icon} className="w-5 h-5" />
    </div>
    <h3 className="text-xl font-serif font-bold text-stone-900 mb-3">{theme.title}</h3>
    <p className="text-stone-600 leading-relaxed">{theme.description}</p>
  </motion.div>
);

export default CoreThemes;